import { Inject, Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { AbstractService } from '@elio/emanager-front/shared/data-access/service';
import { LoggerService } from '@elio/shared/tools/logger';
import { Observable, catchError, forkJoin, map, mergeMap, of, throwError } from 'rxjs';
import { TOPIC_STORAGE, TopicStorage } from './topic-storage/topic-storage.api';

export type TopicType = 'USER' | 'OFFER' | 'HEADQUARTER';

export interface Topic {
  type: TopicType;
  entityId: number;
}

interface SubscribeParams extends Topic {
  token: string;
}

@Injectable({ providedIn: 'root' })
export class MessagingSubscriptionService extends AbstractService {
  private PUSH_MESSAGING_URL = `${this.apiUrl}/push-messaging`;

  constructor(
    private afMessaging: AngularFireMessaging,
    private logger: LoggerService,
    @Inject(TOPIC_STORAGE) private topicStorage: TopicStorage
  ) {
    super();
  }

  subscribeTopic(topic: Topic, cleanupPreviousSubscription = false): Observable<boolean> {
    return this.afMessaging.requestToken.pipe(
      catchError(() => this.firebaseIsNotSupported()),
      mergeMap((fcmToken) => {
        if (fcmToken) {
          let unsubscribe: Observable<boolean>;
          if (cleanupPreviousSubscription) {
            unsubscribe = this.unsubscribeTopics([topic]);
          } else {
            unsubscribe = of(true);
          }
          return unsubscribe.pipe(mergeMap(() => this.subscribeTopicHttp({ ...topic, token: fcmToken }))).pipe(
            map(() => {
              this.topicStorage.saveTopic(topic);
              return true;
            }),
            catchError(() => of(false))
          );
        }
        return of(false);
      })
    );
  }

  unsubscribeAllTopics(): Observable<boolean> {
    const topics = this.topicStorage.getAllSubscribedTopics();
    return !topics || topics.length === 0 ? of(true) : this.unsubscribeTopics(topics);
  }

  unsubscribeTopicsType(type: TopicType): Observable<boolean> {
    const topics = this.topicStorage.getAllSubscribedTopics().filter((topic) => topic.type === type);
    return this.unsubscribeTopics(topics);
  }

  private unsubscribeTopics = (topics: Topic[]): Observable<boolean> =>
    this.afMessaging.getToken.pipe(
      catchError(() => this.firebaseIsNotSupported()),
      mergeMap((token) =>
        token
          ? forkJoin(
              topics.map((topic) =>
                this.unsubscribeTopicHttp({
                  ...topic,
                  token,
                }).pipe(
                  map(() => {
                    this.topicStorage.deleteTopic(topic);
                    return true;
                  }),
                  catchError(() => of(false))
                )
              )
            ).pipe(map((deleted) => deleted.every((key) => key === true)))
          : of(false)
      )
    );

  private subscribeTopicHttp = (params: SubscribeParams): Observable<void> => {
    return this.httpRequest({
      request: () => this.http.post<void>(`${this.PUSH_MESSAGING_URL}/subscribe-topic`, params),
    });
  };

  private unsubscribeTopicHttp = (params: SubscribeParams): Observable<void> => {
    return this.httpRequest({
      request: () => this.http.post<void>(`${this.PUSH_MESSAGING_URL}/unsubscribe-topic`, params),
    });
  };

  private firebaseIsNotSupported(): Observable<null> {
    this.logger.error('Firebase is not supported by browser');
    return throwError(() => null);
  }
}
